import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FaPhone, FaEnvelope, FaFacebook, FaLinkedin, FaInstagram,
  FaMapMarkerAlt, FaClock, FaBars, FaTimes, FaUserCircle,
  FaChevronDown, FaArrowRight, FaCopy, FaCheck
} from "react-icons/fa";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_1280a4a7-60db-4fa6-9a32-77c71d127606/artifacts/9wayhrd3_United%20Taxpert%20Logo.png";

// Email Copy Component
const EmailWithCopy = ({ email, showIcon = true }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = email;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <span className="email-with-copy">
      {showIcon && <FaEnvelope />}
      <a href={`mailto:${email}`}>{email}</a>
      <button 
        onClick={handleCopy} 
        className="copy-email-btn"
        title="Copy email address"
      >
        {copied ? <FaCheck /> : <FaCopy />}
      </button>
    </span>
  );
};

const servicesMenu = [
  { label: "Bookkeeping", path: "/services/bookkeeping" },
  { label: "Tax Planning", path: "/services/tax-planning" },
  { label: "Payroll Services", path: "/services/payroll-services" },
  { label: "Inventory Management", path: "/services/inventory-management" },
  { label: "Financial Reporting & Audit", path: "/services/financial-reporting-audit" },
  { label: "Business Acquisitions", path: "/services/business-acquisitions" },
  { label: "Administrative Services", path: "/services/administrative-services" },
  { label: "Fractional CFO Services", path: "/services/fractional-cfo" },
];

export const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const servicesTimeoutRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setServicesOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleServicesMouseEnter = () => {
    if (servicesTimeoutRef.current) {
      clearTimeout(servicesTimeoutRef.current);
    }
    setServicesOpen(true);
  };

  const handleServicesMouseLeave = () => {
    servicesTimeoutRef.current = setTimeout(() => {
      setServicesOpen(false);
    }, 200);
  };

  const isHome = location.pathname === "/";
  const isAbout = location.pathname === "/about";
  const isServices = location.pathname.startsWith("/services");
  const isPricing = location.pathname === "/pricing";

  return (
    <header className={`site-header ${scrolled ? "scrolled" : ""}`}>
      <div className="header-top">
        <div className="container">
          <div className="header-top-inner">
            <div className="header-contacts">
              <a href="https://calendly.com/unitedtaxpert" target="_blank" rel="noopener noreferrer" className="contact-item">
                <FaPhone /><span>(813) 304-6616</span>
              </a>
              <span className="contact-item">
                <EmailWithCopy email="refunds@unitedtaxpert.com" showIcon={true} />
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="header-main">
        <div className="container">
          <div className="header-main-inner">
            <div className="logo">
              <Link to="/"><img src={LOGO_URL} alt="United Taxpert" className="logo-img" /></Link>
            </div>
            <nav className={`main-nav ${menuOpen ? "open" : ""}`}>
              <Link to="/" className={`nav-link ${isHome ? "active" : ""}`}>Home</Link>
              <Link to="/about" className={`nav-link ${isAbout ? "active" : ""}`}>About Us</Link>

              {/* Services Dropdown */}
              <div
                className={`nav-item-dropdown ${isServices ? "active-parent" : ""}`}
                onMouseEnter={handleServicesMouseEnter}
                onMouseLeave={handleServicesMouseLeave}
              >
                <span className={`nav-link nav-services-toggle ${isServices ? "active" : ""}`}
                  onClick={() => setServicesOpen(!servicesOpen)}>
                  Services <FaChevronDown className={`dropdown-arrow ${servicesOpen ? "open" : ""}`} />
                </span>
                <div className={`dropdown-menu ${servicesOpen ? "show" : ""}`}>
                  {servicesMenu.map(s => (
                    <Link key={s.path} to={s.path} className="dropdown-item"
                      onClick={() => setServicesOpen(false)}>
                      <FaArrowRight className="dd-arrow" /> {s.label}
                    </Link>
                  ))}
                </div>
              </div>

              <a href="#" className="nav-link">Blogs</a>
              <a href="mailto:refunds@unitedtaxpert.com" className="nav-link">Contact Us</a>
              <a href="#" className="nav-link">Join Our Team</a>
            </nav>
            <div className="header-actions">
              <a href="https://calendly.com/unitedtaxpert" target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm">
                <FaPhone /> (813) 304-6616
              </a>
              <a href="#" className="btn btn-outline btn-sm">
                <FaUserCircle /> Login
              </a>
            </div>
            <button className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export const Footer = () => (
  <footer className="site-footer">
    <div className="container">
      <div className="footer-grid">
        <div className="footer-brand">
          <img src={LOGO_URL} alt="United Taxpert" className="footer-logo" />
          <p>Welcome to United Tax Associates LLC, where expert financial solutions and precision meet. Trust us to navigate your finances to your growth.</p>
          <div className="social-links">
            <a href="#" aria-label="Facebook"><FaFacebook /></a>
            <a href="#" aria-label="LinkedIn"><FaLinkedin /></a>
            <a href="#" aria-label="Instagram"><FaInstagram /></a>
          </div>
        </div>
        <div className="footer-col">
          <h4>Useful Links</h4>
          <ul>
            <li><Link to="/">Home</Link></li>
            <li><Link to="/about">About Us</Link></li>
            <li><Link to="/services/bookkeeping">Services</Link></li>
            <li><a href="#">Resources</a></li>
            <li><a href="mailto:refunds@unitedtaxpert.com">Contact Us</a></li>
            <li><a href="#">Join Our Team</a></li>
          </ul>
        </div>
        <div className="footer-col">
          <h4>Resources</h4>
          <ul>
            <li><a href="#">Newsletters</a></li>
            <li><a href="#">Blogs</a></li>
            <li><a href="#">Video Presentations</a></li>
            <li><Link to="/pricing">Pricing Models</Link></li>
            <li><a href="https://www.irs.gov" target="_blank" rel="noopener noreferrer">IRS</a></li>
          </ul>
        </div>
        <div className="footer-col">
          <h4>Contact Information</h4>
          <ul className="contact-list">
            <li><FaPhone /><a href="https://calendly.com/unitedtaxpert" target="_blank" rel="noopener noreferrer">(813) 304-6616</a></li>
            <li><EmailWithCopy email="refunds@unitedtaxpert.com" /></li>
            <li><FaMapMarkerAlt /><span>ATLANTA OFF, 1441 Woodmont Ln NW, Ste 611, Atlanta, GA 30318</span></li>
            <li><FaMapMarkerAlt /><span>TAMPA OFF, 5005 W Laurel St, Ste 100 2098, Tampa, FL, 33607</span></li>
            <li><FaClock /><a href="https://calendly.com/unitedtaxpert" target="_blank" rel="noopener noreferrer">Meeting Scheduler</a></li>
          </ul>
        </div>
      </div>
    </div>
    <div className="footer-bottom">
      <div className="container">
        <div className="footer-bottom-inner">
          <p>&#169; 2024 - 2025. All Rights Reserved.</p>
          <div className="footer-legal">
            <a href="#">Terms &amp; Conditions</a>
            <a href="#">Privacy &amp; Policy</a>
            <a href="#">Legal Disclosures</a>
            <a href="#">FAQs</a>
          </div>
        </div>
      </div>
    </div>
  </footer>
);

export const PageHero = ({ title, bg }) => {
  const LOGO_URL = "https://customer-assets.emergentagent.com/job_1280a4a7-60db-4fa6-9a32-77c71d127606/artifacts/9wayhrd3_United%20Taxpert%20Logo.png";
  
  return (
    <section className="page-hero" style={bg ? { backgroundImage: `url(${bg})` } : {}}>
      <div className="page-hero-overlay" />
      <div className="container page-hero-container">
        <div className="page-hero-content">
          <h1>{title}</h1>
          <div className="breadcrumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <span>{title}</span>
          </div>
        </div>
        <div className="page-hero-logo-animated">
          <img src={LOGO_URL} alt="United Taxpert" className="page-hero-logo-img" />
        </div>
      </div>
    </section>
  );
};

export const TroubleSection = ({ href = "mailto:refunds@unitedtaxpert.com" }) => (
  <section className="trouble-section">
    <div className="trouble-overlay" />
    <div className="container trouble-content">
      <div className="trouble-text">
        <h2>Having Trouble Evaluating Your Finances?</h2>
        <p>Gain clarity and confidence with United Taxpert. Our experts help you analyze your financial standing, uncover hidden opportunities, and identify areas for improvement. From cash flow evaluation to tax efficiency strategies, we provide the insights you need to make smarter, data-driven financial decisions.</p>
        <a href={href} className="btn btn-primary">Contact Us Now</a>
      </div>
      <div className="trouble-image">
        <img src="https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=500&auto=format&fit=crop&q=80" alt="Financial advisor" />
      </div>
    </div>
  </section>
);

export { servicesMenu };
